import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './LocazioniListaPage.api';

type Periodicita = 'MENSILE' | 'BIMESTRALE' | 'TRIMESTRALE' | 'SEMESTRALE' | 'ANNUALE';

interface LocazioneRow {
  id: number;
  societaId: number;
  sedeId: number;
  fornitoreId: number;
  dataInizio: string;
  durataAnniIniziale: number;
  durataAnniProroga: number;
  preavvisoDisdettaMesi: number;
  importoCanone: string | number;
  periodicitaPagamento: Periodicita;
  rivalutazioneIstat: boolean;
  rivalutazioneIstatPerc: string | number | null;
  disdettata: boolean;
  dataDisdetta: string | null;
  attivo: boolean;
  societa: { id: number; nome: string };
  sede: { id: number; nome: string; citta: string | null };
  fornitore: { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null };
}

interface ListaResp { success: boolean; data: LocazioneRow[] }

const PERIODICITA_LABEL: Record<Periodicita, string> = {
  MENSILE: 'Mensile',
  BIMESTRALE: 'Bimestrale',
  TRIMESTRALE: 'Trimestrale',
  SEMESTRALE: 'Semestrale',
  ANNUALE: 'Annuale',
};

function fornitoreLabel(f: LocazioneRow['fornitore']): string {
  if (f.ragioneSociale) return f.ragioneSociale;
  const nc = [f.nome, f.cognome].filter(Boolean).join(' ').trim();
  return nc || f.codice;
}

function addYears(iso: string, years: number): Date {
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d;
}

function statoLocazione(l: LocazioneRow): { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' } {
  if (!l.attivo) return { label: 'Disattivata', variant: 'neutral' };
  if (l.disdettata) return { label: 'Disdettata', variant: 'danger' };
  const oggi = new Date();
  const finePrima = addYears(l.dataInizio, l.durataAnniIniziale);
  const fineProroga = addYears(l.dataInizio, l.durataAnniIniziale + l.durataAnniProroga);
  if (oggi > fineProroga) return { label: 'Scaduta', variant: 'danger' };
  // entro N mesi dalla prima scadenza → in finestra disdetta
  const sogliaPreavviso = new Date(finePrima);
  sogliaPreavviso.setMonth(sogliaPreavviso.getMonth() - (l.preavvisoDisdettaMesi || 0));
  if (oggi >= sogliaPreavviso && oggi <= finePrima) return { label: 'In preavviso', variant: 'warning' };
  if (oggi > finePrima) return { label: 'In proroga', variant: 'warning' };
  return { label: 'Attiva', variant: 'success' };
}

function fmtData(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

export default function LocazioniListaPage() {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const {
    societaId,
    setSocietaId,
    listaSocieta,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [data, setData] = useState<LocazioneRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtroQ, setFiltroQ] = useState('');
  const [filtroStato, setFiltroStato] = useState<'tutte' | 'attive' | 'disattivate'>('attive');

  useEffect(() => {
    if (!pronta) return;
    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronta, societaId, filtroQ, filtroStato]);

  // @api GET /api/locazioni?societaId=&sedeId=&fornitoreId=&q=&attivo= -> {"success":true,"data":[{...locazione}]}
  async function fetchData() {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (societaId) params.append('societaId', societaId);
      if (filtroQ.trim()) params.append('q', filtroQ.trim());
      if (filtroStato === 'attive') params.append('attivo', 'true');
      if (filtroStato === 'disattivate') params.append('attivo', 'false');
      const res = await apiFetch<ListaResp>(`/api/locazioni?${params.toString()}`);
      setData(res.data || []);
    } catch (err) {
      console.error('[LocazioniListaPage] fetch error', err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }

  const canEdit = can('management.locazioni', 'edit');

  const rows = useMemo(() => data, [data]);

  const columns = useMemo<BopExcelColumn<LocazioneRow>[]>(() => [
    {
      key: 'societa',
      header: 'Società',
      accessor: (l) => l.societa?.nome || '',
      filter: { type: 'text' },
      render: (l) => l.societa?.nome ?? '—',
    },
    {
      key: 'sede',
      header: 'Sede',
      accessor: (l) => `${l.sede?.nome || ''} ${l.sede?.citta || ''}`.trim(),
      filter: { type: 'text' },
      render: (l) => `${l.sede?.nome ?? '—'}${l.sede?.citta ? ` (${l.sede.citta})` : ''}`,
    },
    {
      key: 'fornitore',
      header: 'Fornitore',
      accessor: (l) => fornitoreLabel(l.fornitore),
      filter: { type: 'text' },
      render: (l) => fornitoreLabel(l.fornitore),
    },
    {
      key: 'dataInizio',
      header: 'Inizio',
      accessor: (l) => l.dataInizio || '',
      filter: { type: 'dateRange' },
      width: '110px',
      render: (l) => fmtData(l.dataInizio),
    },
    {
      key: 'finePrima',
      header: 'Fine 1ª scad.',
      accessor: (l) => addYears(l.dataInizio, l.durataAnniIniziale).toISOString(),
      filter: { type: 'dateRange' },
      width: '120px',
      render: (l) => fmtData(addYears(l.dataInizio, l.durataAnniIniziale).toISOString()),
    },
    {
      key: 'canone',
      header: 'Canone',
      accessor: (l) => Number(l.importoCanone),
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right',
      render: (l) => formatCurrency(Number(l.importoCanone)),
    },
    {
      key: 'periodicita',
      header: 'Periodicità',
      accessor: (l) => l.periodicitaPagamento,
      filter: {
        type: 'enum',
        options: (Object.keys(PERIODICITA_LABEL) as Periodicita[])
          .map(p => ({ value: p, label: PERIODICITA_LABEL[p] })),
      },
      width: '120px',
      render: (l) => PERIODICITA_LABEL[l.periodicitaPagamento],
    },
    {
      key: 'istat',
      header: 'ISTAT',
      accessor: (l) => l.rivalutazioneIstat ? 'SI' : 'NO',
      filter: {
        type: 'enum',
        options: [
          { value: 'SI', label: 'Sì' },
          { value: 'NO', label: 'No' },
        ],
      },
      width: '90px',
      render: (l) => l.rivalutazioneIstat
        ? <span className="bop-badge bop-badge-info">{l.rivalutazioneIstatPerc != null ? `${Number(l.rivalutazioneIstatPerc)}%` : 'sì'}</span>
        : <span className="bop-text-muted">no</span>,
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (l) => statoLocazione(l).label,
      filter: {
        type: 'enum',
        options: [
          { value: 'Attiva',       label: 'Attiva' },
          { value: 'In preavviso', label: 'In preavviso' },
          { value: 'In proroga',   label: 'In proroga' },
          { value: 'Disdettata',   label: 'Disdettata' },
          { value: 'Scaduta',      label: 'Scaduta' },
          { value: 'Disattivata',  label: 'Disattivata' },
        ],
      },
      width: '130px',
      render: (l) => {
        const stato = statoLocazione(l);
        return <span className={`bop-badge bop-badge-${stato.variant}`}>{stato.label}</span>;
      },
    },
  ], []);

  return (
    <Layout title="Locazioni immobiliari">
      <div className="bop-page-header">
        <h1 className="bop-page-title">Locazioni</h1>
        {canEdit && (
          <button
            className="bop-btn-primary"
            onClick={() => navigate('/management/locazioni/nuovo')}
          >
            + Nuova locazione
          </button>
        )}
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select
            className="bop-select"
            value={societaId}
            onChange={(e) => setSocietaId(e.target.value)}
          >
            <option value="">Tutte le società</option>
            {listaSocieta.map((s) => (
              <option key={s.id} value={s.id}>{s.nome}</option>
            ))}
          </select>
        )}
        <input
          className="bop-input"
          placeholder="Cerca sede, fornitore, note…"
          value={filtroQ}
          onChange={(e) => setFiltroQ(e.target.value)}
        />
        <select
          className="bop-select"
          value={filtroStato}
          onChange={(e) => setFiltroStato(e.target.value as typeof filtroStato)}
        >
          <option value="attive">Solo attive</option>
          <option value="disattivate">Solo disattivate</option>
          <option value="tutte">Tutte</option>
        </select>
      </div>

      <BopExcelTable<LocazioneRow>
        data={rows}
        loading={isLoading}
        rowKey={(l) => l.id}
        onRowClick={(l) => navigate(`/management/locazioni/${l.id}`)}
        initialSort={{ key: 'dataInizio', dir: 'desc' }}
        emptyState={<div className="bop-empty-state">Nessuna locazione trovata</div>}
        columns={columns}
      />

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
